import React, { useState, useRef, useEffect } from "react";
import { Modal, Button, Form, InputGroup } from "react-bootstrap";
import { FaPaperPlane, FaRobot, FaUser } from "react-icons/fa";
import Spinner from "react-bootstrap/Spinner";
import { AIChatbot } from "../../../features/ai_chatbots/aiChatbotsService";
import apiClient from "../../../api/apiClient";
import { useTranslation } from "react-i18next";
import { Icon } from "../../../components/common/Icon";

interface AIChatbotTestModalProps {
  show: boolean;
  onHide: () => void;
  chatbot: AIChatbot | null;
}

interface Message {
  sender: "user" | "bot";
  text: string;
  timestamp: Date;
}

const AIChatbotTestModal: React.FC<AIChatbotTestModalProps> = ({
  show,
  onHide,
  chatbot,
}) => {
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const translate = useTranslation();

  // Kaydırma için efekt
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Modal her açıldığında yeni bir session ID oluştur
  useEffect(() => {
    if (show) {
      // Chatbot açılış mesajını göster
      if (chatbot?.initial_message) {
        setMessages([
          {
            sender: "bot",
            text: translate.t(chatbot.initial_message),
            timestamp: new Date(),
          },
        ]);
      } else {
        setMessages([]);
      }
    }
  }, [show, chatbot]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || !chatbot) return;

    // Kullanıcı mesajını ekle
    const userMessage: Message = {
      sender: "user",
      text: message,
      timestamp: new Date(),
    };
    setMessages([...messages, userMessage]);
    setMessage("");
    setIsLoading(true);

    try {
      // API isteği gönder - widget_key kullanarak
      const widgetKey = chatbot.widget_key || chatbot.id;

      // Token al
      const token = localStorage.getItem("token");

      const apiUrl =
        process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";

      const response = await apiClient.post(`/public/chatbot/${widgetKey}/chat`, {
        message: message.trim(),
        mode: "test",
      });

      console.log("Chatbot response:", response.data);

      // Bot cevabını ekle
      const botMessage: Message = {
        sender: "bot",
        text:
          (response as any).data?.message || (response as any)?.message || translate.t("not_response_for_ai_chatbot"),
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error("Error sending message:", error);
      // Hata mesajı ekle
      const errorMessage: Message = {
        sender: "bot",
        text: translate.t("error_sending_message"),
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const handleClose = () => {
    setMessages([]);
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      className="chatbot-test-modal"
    >
      <Modal.Header className="chatbot-modal-header">
        <div className="d-flex align-items-center gap-3">
          <div className="chatbot-avatar">
            <Icon icon={FaRobot} />
          </div>
          <div>
            <Modal.Title className="chatbot-modal-title">
              {chatbot?.name}
            </Modal.Title>
            <small className="text-muted">Test Mode - AI Assistant</small>
          </div>
        </div>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={handleClose}
          className="rounded-circle p-2"
        >
          ×
        </Button>
      </Modal.Header>

      <Modal.Body className="p-0 chatbot-modal-body">
        <div className="chat-container">
          {messages.length === 0 ? (
            <div className="empty-chat-state">
              <div className="empty-chat-icon">
                <Icon icon={FaRobot} />
              </div>
              <h6 className="empty-chat-title">Sohbeti Başlatın</h6>
              <p className="empty-chat-text">
                AI asistanınızla konuşmaya başlamak için aşağıdan bir mesaj
                gönderin.
              </p>
            </div>
          ) : (
            <div className="messages-container">
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`message-wrapper ${msg.sender === "user" ? "user-message" : "bot-message"}`}
                >
                  <div className="message-avatar">
                    <Icon icon={msg.sender === "user" ? FaUser : FaRobot} />
                  </div>
                  <div className="message-content">
                    <div className="message-bubble">
                      <div className="message-text">{msg.text}</div>
                      <div className="message-time">
                        {formatTime(msg.timestamp)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="message-wrapper bot-message">
                  <div className="message-avatar">
                    <Icon icon={FaRobot} />
                  </div>
                  <div className="message-content">
                    <div className="message-bubble typing-indicator">
                      <div className="typing-dots">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className="chatbot-modal-footer">
        <Form className="w-100" onSubmit={handleSendMessage}>
          <InputGroup className="message-input-group">
            <Form.Control
              className="message-input"
              placeholder={
                translate.t("write_your_message") || "Mesajınızı yazın..."
              }
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={isLoading || !chatbot}
            />
            <Button
              variant="primary"
              type="submit"
              disabled={isLoading || !message.trim() || !chatbot}
              className="send-button"
            >
              <Icon icon={FaPaperPlane} />
            </Button>
          </InputGroup>
        </Form>
      </Modal.Footer>

      <style>{`
        .chatbot-test-modal .modal-content {
          border: none;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
        }

        .chatbot-modal-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 20px 24px;
          border: none;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .chatbot-avatar {
          width: 48px;
          height: 48px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          color: white;
          backdrop-filter: blur(10px);
        }

        .chatbot-modal-title {
          color: white;
          margin: 0;
          font-size: 18px;
          font-weight: 600;
        }

        .chatbot-modal-body {
          height: 500px;
          background: #f8fafc;
        }

        .chat-container {
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        .empty-chat-state {
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          text-align: center;
        }

        .empty-chat-icon {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 32px;
          color: white;
          margin-bottom: 20px;
          opacity: 0.8;
        }

        .empty-chat-title {
          color: #1e293b;
          margin-bottom: 8px;
          font-weight: 600;
        }

        .empty-chat-text {
          color: #64748b;
          margin: 0;
          max-width: 300px;
          line-height: 1.5;
        }

        .messages-container {
          flex: 1;
          overflow-y: auto;
          padding: 20px;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .message-wrapper {
          display: flex;
          gap: 12px;
          max-width: 85%;
        }

        .message-wrapper.user-message {
          align-self: flex-end;
          flex-direction: row-reverse;
        }

        .message-wrapper.bot-message {
          align-self: flex-start;
        }

        .message-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          flex-shrink: 0;
          margin-top: 4px;
        }

        .user-message .message-avatar {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .bot-message .message-avatar {
          background: #e2e8f0;
          color: #64748b;
        }

        .message-content {
          flex: 1;
          min-width: 0;
        }

        .message-bubble {
          padding: 12px 16px;
          border-radius: 18px;
          position: relative;
          word-wrap: break-word;
        }

        .user-message .message-bubble {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-bottom-right-radius: 6px;
        }

        .bot-message .message-bubble {
          background: white;
          color: #1e293b;
          border: 1px solid #e2e8f0;
          border-bottom-left-radius: 6px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        }

        .message-text {
          line-height: 1.5;
          margin-bottom: 4px;
        }

        .message-time {
          font-size: 11px;
          opacity: 0.7;
          text-align: right;
        }

        .bot-message .message-time {
          color: #64748b;
        }

        .typing-indicator {
          background: white !important;
          border: 1px solid #e2e8f0 !important;
          padding: 16px !important;
        }

        .typing-dots {
          display: flex;
          gap: 4px;
          align-items: center;
        }

        .typing-dots span {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #64748b;
          animation: typing 1.4s infinite ease-in-out;
        }

        .typing-dots span:nth-child(1) {
          animation-delay: -0.32s;
        }
        .typing-dots span:nth-child(2) {
          animation-delay: -0.16s;
        }

        @keyframes typing {
          0%,
          80%,
          100% {
            transform: scale(0.8);
            opacity: 0.5;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }

        .chatbot-modal-footer {
          background: white;
          border: none;
          padding: 20px 24px;
          border-top: 1px solid #e2e8f0;
        }

        .message-input-group {
          border-radius: 25px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          border: 1px solid #e2e8f0;
        }

        .message-input {
          border: none;
          padding: 12px 20px;
          font-size: 14px;
          background: #f8fafc;
        }

        .message-input:focus {
          box-shadow: none;
          background: white;
          border-color: transparent;
        }

        .send-button {
          border: none;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 12px 20px;
          border-radius: 0;
          transition: all 0.2s ease;
        }

        .send-button:hover:not(:disabled) {
          background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
          transform: translateY(-1px);
        }

        .send-button:disabled {
          background: #e2e8f0;
          color: #94a3b8;
        }

        /* Scrollbar Styling */
        .messages-container::-webkit-scrollbar {
          width: 6px;
        }

        .messages-container::-webkit-scrollbar-track {
          background: transparent;
        }

        .messages-container::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }

        .messages-container::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .chatbot-test-modal .modal-dialog {
            margin: 10px;
            max-width: calc(100% - 20px);
          }

          .chatbot-modal-body {
            height: 400px;
          }

          .chatbot-modal-header {
            padding: 16px 20px;
          }

          .chatbot-avatar {
            width: 40px;
            height: 40px;
            font-size: 18px;
          }

          .chatbot-modal-title {
            font-size: 16px;
          }

          .messages-container {
            padding: 16px;
            gap: 12px;
          }

          .message-wrapper {
            max-width: 90%;
          }

          .message-avatar {
            width: 32px;
            height: 32px;
            font-size: 12px;
          }

          .message-bubble {
            padding: 10px 14px;
          }

          .chatbot-modal-footer {
            padding: 16px 20px;
          }

          .message-input {
            padding: 10px 16px;
            font-size: 14px;
          }

          .send-button {
            padding: 10px 16px;
          }
        }

        /* Dark Theme Support */
        [data-bs-theme="dark"] .chatbot-modal-body {
          background: #0f172a;
        }

        [data-bs-theme="dark"] .empty-chat-title {
          color: #f8fafc;
        }

        [data-bs-theme="dark"] .empty-chat-text {
          color: #cbd5e1;
        }

        [data-bs-theme="dark"] .bot-message .message-bubble {
          background: #1e293b;
          color: #f8fafc;
          border-color: #334155;
        }

        [data-bs-theme="dark"] .bot-message .message-avatar {
          background: #334155;
          color: #cbd5e1;
        }

        [data-bs-theme="dark"] .bot-message .message-time {
          color: #94a3b8;
        }

        [data-bs-theme="dark"] .message-input {
          background: #1e293b;
          color: #f8fafc;
          border-color: #334155;
        }

        [data-bs-theme="dark"] .message-input:focus {
          background: #334155;
        }

        [data-bs-theme="dark"] .chatbot-modal-footer {
          background: #0f172a;
          border-color: #334155;
        }

        [data-bs-theme="dark"] .message-input-group {
          border-color: #334155;
        }

        [data-bs-theme="dark"] .typing-indicator {
          background: #1e293b !important;
          border-color: #334155 !important;
        }

        [data-bs-theme="dark"] .typing-dots span {
          background: #94a3b8;
        }
      `}</style>
    </Modal>
  );
};

export default AIChatbotTestModal;
